import {
  Button,
  Dialog,
  InputLabel,
  ListItem,
  DialogNavigation,
  DialogContent,
  DialogCTA,
} from "@sellar-io/components";
import { useState } from "react";
import { InfiniteList } from "../../../components/infiniteLoading/InfiniteList";
import { twMerge } from "tailwind-merge";
import { useFulfillmentTypeList } from "../../../hooks/api/v2/fulfillmentType/useFulfillmentTypeList";

type DeliveryIdsType = {
  DrayDeliveryMethodId: number | null;
  CourierDeliveryMethodId: number | null;
  PalletDeliveryMethodId: number | null;
};

export const AssignDeliveryDialog = ({
  active,
  setActive,
  loading,
  deliveryIds,
  onClick,
}: {
  active: boolean;
  setActive: (active: boolean) => void;
  loading: boolean;
  deliveryIds: DeliveryIdsType;
  onClick: (deliveryIds: DeliveryIdsType) => void;
}) => {
  const [updatedDeliveryIds, setUpdatedDeliveryIds] =
    useState<DeliveryIdsType>(deliveryIds);

  const onSubmit = () => {
    onClick(updatedDeliveryIds);
  };

  return (
    <Dialog active={active} onDismiss={() => setActive(false)}>
      <DialogNavigation
        leftIcon="CHEVRON_DOWN"
        onClickLeftIcon={() => setActive(false)}
        title="Assign price list"
        description="Your customer can be assigned any of the price lists you have defined"
      />
      <DialogContent className="flex min-h-75 flex-col gap-4">
        {active && (
          <Content
            updatedDeliveryIds={updatedDeliveryIds}
            setUpdatedDeliveryIds={setUpdatedDeliveryIds}
          />
        )}
      </DialogContent>
      <DialogCTA>
        <Button onClick={onSubmit} loading={loading}>
          Confirm
        </Button>
      </DialogCTA>
    </Dialog>
  );
};

const Content = ({
  updatedDeliveryIds,
  setUpdatedDeliveryIds,
}: {
  updatedDeliveryIds: DeliveryIdsType;
  setUpdatedDeliveryIds: React.Dispatch<React.SetStateAction<DeliveryIdsType>>;
}) => {
  const drayDeliveryMethodsQuery = useFulfillmentTypeList({
    query: [
      {
        type: "FulfillmentType",
        modifier: "EQUALS",
        value: "direct",
      },
    ],
    limit: 10,
  });

  const drayDeliveryMethods = drayDeliveryMethodsQuery.data?.pages.flat() ?? [];

  const courierDeliveryMethodsQuery = useFulfillmentTypeList({
    query: [
      {
        type: "FulfillmentType",
        modifier: "EQUALS",
        value: "courier",
      },
    ],
    limit: 10,
  });

  const courierDeliveryMethods =
    courierDeliveryMethodsQuery.data?.pages.flat() ?? [];

  const palletDeliveryMethodsQuery = useFulfillmentTypeList({
    query: [
      {
        type: "FulfillmentType",
        modifier: "EQUALS",
        value: "haulier",
      },
    ],
    limit: 10,
  });

  const palletDeliveryMethods =
    palletDeliveryMethodsQuery.data?.pages.flat() ?? [];

  return (
    <>
      <InputLabel>Direct</InputLabel>
      <InfiniteList
        query={drayDeliveryMethodsQuery}
        className="flex flex-col gap-2"
      >
        {drayDeliveryMethods.map((drayDeliveryMethod) => {
          const selected =
            drayDeliveryMethod.id === updatedDeliveryIds.DrayDeliveryMethodId;

          return (
            <ListItem
              key={drayDeliveryMethod.id}
              title={drayDeliveryMethod.name ?? "Direct"}
              subtitle={""}
              icon={selected ? "CHECK" : "TRUCK"}
              actionText="Preview"
              onClick={() => {
                setUpdatedDeliveryIds((prev) => ({
                  ...prev,
                  DrayDeliveryMethodId: selected ? null : drayDeliveryMethod.id,
                }));
              }}
              className={twMerge(
                "bg-white p-4 hover:bg-zinc-50",
                selected ? "bg-zinc-50" : "",
              )}
              iconClassName={selected ? "border-zinc-800 bg-zinc-200" : ""}
            />
          );
        })}
      </InfiniteList>

      <InputLabel>Courier</InputLabel>
      <InfiniteList
        query={courierDeliveryMethodsQuery}
        className="flex flex-col gap-2"
      >
        {courierDeliveryMethods.map((courierDeliveryMethod) => {
          const selected =
            courierDeliveryMethod.id ===
            updatedDeliveryIds.CourierDeliveryMethodId;

          return (
            <ListItem
              key={courierDeliveryMethod.id}
              title={courierDeliveryMethod.name ?? "Courier"}
              subtitle={""}
              icon={selected ? "CHECK" : "PACKAGE_VARIANT_CLOSED_FILL"}
              actionText="Preview"
              onClick={() => {
                setUpdatedDeliveryIds((prev) => ({
                  ...prev,
                  CourierDeliveryMethodId: selected
                    ? null
                    : courierDeliveryMethod.id,
                }));
              }}
              className={twMerge(
                "bg-white p-4 hover:bg-zinc-50",
                selected ? "bg-zinc-50" : "",
              )}
              iconClassName={selected ? "border-zinc-800 bg-zinc-200" : ""}
            />
          );
        })}
      </InfiniteList>

      <InputLabel>Pallet</InputLabel>
      <InfiniteList
        query={palletDeliveryMethodsQuery}
        className="mb-8 flex flex-col gap-2"
      >
        {palletDeliveryMethods.map((palletDeliveryMethod) => {
          const selected =
            palletDeliveryMethod.id ===
            updatedDeliveryIds.PalletDeliveryMethodId;

          return (
            <ListItem
              key={palletDeliveryMethod.id}
              title={palletDeliveryMethod.name ?? "Haulier"}
              subtitle={""}
              icon={selected ? "CHECK" : "PALLET"}
              actionText="Preview"
              onClick={() => {
                setUpdatedDeliveryIds((prev) => ({
                  ...prev,
                  PalletDeliveryMethodId: selected
                    ? null
                    : palletDeliveryMethod.id,
                }));
              }}
              className={twMerge(
                "bg-white p-4 hover:bg-zinc-50",
                selected ? "bg-zinc-50" : "",
              )}
              iconClassName={selected ? "border-zinc-800 bg-zinc-200" : ""}
            />
          );
        })}
      </InfiniteList>
    </>
  );
};
